/**
 * Shared locale message helpers.
 *
 * After the `@nuxtjs/i18n` removal, components resolve strings straight from
 * their `*.locale.json` files. These helpers centralize the two operations that
 * every component needs so they cannot drift apart (e.g. first-occurrence vs
 * global interpolation):
 *   1. resolving a dot-delimited key against a nested locale object;
 *   2. interpolating `{param}` placeholders.
 */

export type LocaleParams = Record<string, string | undefined>;

/**
 * Resolves a dot-delimited `key` against a nested locale object and returns the
 * string leaf, or `''` when the path is missing or does not point to a string.
 */
export function getLocaleString(
	source: Record<string, unknown> | undefined,
	key: string
): string {
	const value = key
		.split('.')
		.reduce<unknown>(
			(acc, part) =>
				acc && typeof acc === 'object'
					? (acc as Record<string, unknown>)[part]
					: undefined,
			source
		);

	return typeof value === 'string' ? value : '';
}

/**
 * Replaces every `{param}` placeholder in `template` with its value.
 *
 * Substitution is global (repeated placeholders are all replaced) and safe:
 * values are inserted verbatim, so `$`-sequences carry no special meaning.
 * Placeholders without a matching param are left untouched.
 */
export function interpolate(
	template: string,
	params: LocaleParams = {}
): string {
	return template.replace(/\{(\w+)\}/g, (match, name: string) =>
		name in params ? String(params[name] ?? '') : match
	);
}

/**
 * Builds a translator bound to a locale `source` object.
 *
 * - `translate(key, params)` resolves a root-level key and interpolates params.
 * - `translateByContext(key, params)` prefixes the key with `context`
 *   (e.g. `'property'` / `'condo'`) before resolving.
 */
export function createLocaleTranslator(
	source: Record<string, unknown>,
	context?: string
) {
	const translate = (key: string, params: LocaleParams = {}): string =>
		interpolate(getLocaleString(source, key), params);

	const translateByContext = (key: string, params: LocaleParams = {}): string =>
		translate(context ? `${context}.${key}` : key, params);

	return { translate, translateByContext };
}
